import { LEGACY_KEYS } from "@hachi/core";
import { readJson, writeJson } from "./storage";
import type { KeyValue } from "./storage";
import type { Background } from "./background";

export type Sku = "enthusiast" | "shopMonthly" | "shopYearly" | "shopLifetime" | "addDevice" | "addCars" | "shopDevice";

/** Prices, buy links and trial settings, public from `GET /api/account?plans=1` (`STORE`). */
export interface StoreConfig {
  currency: string;
  prices: Record<Sku, number>;
  links: Partial<Record<Sku, string>>;
  launchNote?: string;
  trial?: { enabled?: boolean; enthusiast?: number; shop?: number };
  /** The site background the owner chose in the admin. */
  theme?: { bg?: Background; logo?: string };
}

export const STORE_DEFAULT: StoreConfig = {
  currency: "USD",
  prices: { enthusiast: 8, shopMonthly: 14, shopYearly: 129, shopLifetime: 299, addDevice: 5, addCars: 7, shopDevice: 9 },
  links: {},
  launchNote: "",
  trial: { enabled: true, enthusiast: 3, shop: 7 },
};

/** The last prices seen, so screens work offline. */
export function readStore(store: KeyValue): StoreConfig {
  const s = readJson<StoreConfig>(store, LEGACY_KEYS.store);
  return s?.prices ? s : STORE_DEFAULT;
}

/** Fetches current prices and saves them. Keeps the last known ones on any failure. */
export async function loadStore(store: KeyValue, apiPath = "api/account", f: typeof fetch = (...a) => fetch(...a)): Promise<StoreConfig> {
  try {
    const r = await f(`${apiPath}?plans=1`, { cache: "no-cache" });
    const j = (await r.json()) as { ok?: boolean; store?: StoreConfig };
    if (j.ok && j.store) { writeJson(store, LEGACY_KEYS.store, j.store); return j.store; }
  } catch {
    // Offline or server trouble.
  }
  return readStore(store);
}

/** Trial settings with today's defaults (`trialCfg`). */
export function trialConfig(s: StoreConfig) {
  const t = s.trial ?? {};
  return { enabled: t.enabled !== false, enthusiast: t.enthusiast || 3, shop: t.shop || 7 };
}

/** Whole-unit prices without decimals, others with two (`fmtPrice`). */
export function formatPrice(n: number, currency = "USD", locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `$${n}`;
  }
}

/** Checkout address with only this option in the cart and the account's email filled in, so the purchase attaches itself (`gumUrl`). */
export function buyUrl(s: StoreConfig, sku: Sku, email?: string): string {
  const href = s.links[sku];
  if (!href) return "";
  try {
    const u = new URL(href);
    u.searchParams.set("wanted", "true");
    const em = email?.trim();
    if (em && !u.searchParams.has("email")) u.searchParams.set("email", em);
    return u.toString();
  } catch {
    return href;
  }
}

export type TrialPlan = "enthusiast" | "shop";

/** "3 days left", "ends in 5 hours", "ends now" (`trialLeftText`). */
export function trialLeft(endsAt: string, now: number): string {
  const ms = Date.parse(endsAt) - now;
  if (ms <= 0) return "ends now";
  const d = Math.ceil(ms / 864e5), h = Math.ceil(ms / 36e5);
  return d <= 1 ? `ends in ${h} ${h === 1 ? "hour" : "hours"}` : `${d} days left`;
}

export interface EntryIntent {
  /** A trial the person came for from the website (`?trial=`, or an old `?demo=1` link). */
  trial: TrialPlan | "";
  fromCheckout: boolean;
  /** The query string with those parameters removed. */
  rest: string;
}

/** Reads what the website asked for in the address. The old no-account demo is gone: `?demo=1` means "offer the Enthusiast trial". */
export function readEntryIntent(search: string): EntryIntent {
  const qs = new URLSearchParams(search);
  const want = qs.get("trial");
  const trial: EntryIntent["trial"] = want === "shop" || want === "enthusiast" ? want : qs.get("demo") === "1" ? "enthusiast" : "";
  const fromCheckout = qs.get("from") === "checkout";
  qs.delete("trial");
  qs.delete("demo");
  if (fromCheckout) qs.delete("from");
  const rest = qs.toString();
  return { trial, fromCheckout, rest: rest ? `?${rest}` : "" };
}
